// Message log — the scrollable area between header and composer.
// Renders the persistent skills anchor, then either the empty state + quick
// actions or the message list (checkpoints in place), and lastly the summarize
// progress card.
import { Bot, Folder, MessageSquare, Zap } from 'lucide-react';
import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { chatStore, useChatStore } from '@/lib/chatStore';
import { MessageRow } from './MessageRow';
import { QuestionCard } from './QuestionCard';
import { QuickAction } from './QuickAction';
import { SummarizeProgressCard } from './SummarizeProgressCard';

interface ChatMessageLogProps {
  logRef: RefObject<HTMLDivElement | null>;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
}

export function ChatMessageLog({ logRef, textareaRef }: ChatMessageLogProps) {
  const { messages, earlier, conversationMeta, summarizeJob, pendingQuestion } = useChatStore();
  // Distance from the bottom when "Show earlier messages" was pressed, restored
  // before paint once the page is in — Safari has no CSS scroll anchoring.
  const fromBottom = useRef<number | null>(null);
  useLayoutEffect(() => {
    const log = logRef.current;
    if (!log || fromBottom.current === null) return;
    log.scrollTop = log.scrollHeight - fromBottom.current;
    fromBottom.current = null;
  }, [logRef, messages]);

  const showEarlier = async (): Promise<void> => {
    const log = logRef.current;
    fromBottom.current = log ? log.scrollHeight - log.scrollTop : null;
    if (!(await chatStore.loadEarlierMessages())) fromBottom.current = null;
  };
  const loadedSkills = conversationMeta.loadedSkills ?? [];
  const isEmpty = messages.length === 0 && !summarizeJob?.running;

  return (
    <div
      ref={logRef}
      data-testid="chat-message-log"
      className="h-full overflow-y-auto px-3 py-4"
      role="log"
      aria-live="polite"
    >
      {/* Persistent conversation header — the skills loaded on the
          conversation belong in the header, not the live stream. */}
      {loadedSkills.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Skills</span>
          {loadedSkills.map((skill) => (
            <span
              key={skill}
              className="rounded-md border border-border/60 bg-muted/20 px-2 py-0.5 font-mono text-xs text-muted-foreground"
            >
              {skill}
            </span>
          ))}
        </div>
      )}
      {isEmpty ? (
        <EmptyState textareaRef={textareaRef} />
      ) : (
        <div className="flex flex-col gap-3">
          {earlier !== null ? <EarlierMessagesButton onLoad={showEarlier} /> : null}
          {messages.map((message) =>
            message.role === 'system' ? (
              <MessageRow key={message.id} message={message} onDismiss={() => chatStore.dismissMessage(message.id)} />
            ) : (
              <MessageRow key={message.id} message={message} />
            ),
          )}
        </div>
      )}

      {/* Transient cards — pinned BELOW the message stream so they stay
          visible at the bottom where the auto-scroll anchors the viewport. */}
      {pendingQuestion ? (
        <div className="mt-3">
          <QuestionCard question={pendingQuestion} />
        </div>
      ) : null}
      {summarizeJob?.running ? (
        <div className="mt-3">
          <SummarizeProgressCard job={summarizeJob} />
        </div>
      ) : null}
    </div>
  );
}

function EarlierMessagesButton({ onLoad }: { onLoad: () => Promise<void> }) {
  const [loading, setLoading] = useState(false);
  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      await onLoad();
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      data-testid="chat-show-earlier"
      className="self-center rounded-md border border-border/60 px-3 py-1 text-xs text-muted-foreground hover:bg-muted disabled:opacity-60"
      disabled={loading}
      onClick={() => void load()}
    >
      {loading ? 'Loading earlier messages…' : 'Show earlier messages'}
    </button>
  );
}

function EmptyState({ textareaRef }: { textareaRef: RefObject<HTMLTextAreaElement | null> }) {
  // Lightweight empty state — the right column is always present alongside
  // every page, so the placeholder must not dominate. QuickActions are the
  // affordance; the hint above is one line.
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3">
      <p className="text-xs text-muted-foreground">Try a quick action or type below.</p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <QuickAction icon={Bot} label="What's running right now?" textareaRef={textareaRef} />
        <QuickAction icon={Zap} label="Check for bugs or issues" textareaRef={textareaRef} />
        <QuickAction icon={Folder} label="Review recent changes" textareaRef={textareaRef} />
        <QuickAction icon={MessageSquare} label="What's coming up on the calendar?" textareaRef={textareaRef} />
      </div>
    </div>
  );
}
